import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, PiggyBank } from 'lucide-react'
import { toast } from 'sonner'
import {
  deleteContribution,
  getEarmarks,
  getGoalHistory,
  listContributions,
  listGoals,
  pauseGoal,
  reactivateGoal,
  resumeGoal,
} from '@/api/goals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { CategoryIcon } from '@/lib/categoryIcon'
import { formatDate, formatMonth } from '@/lib/formatDate'
import { DEFAULT_GOAL_ICON } from '@/lib/goalIconOptions'
import { daysUntil, paceBadge, timeGoneFraction, trackedIn } from '@/lib/goalPace'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { ALL_TIME, transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'
import { PANEL } from '@/components/budget/MonthlyBudgets'
import {
  CompleteGoalConfirm,
  ContributeModal,
  DeleteGoalConfirm,
  GoalFormSheet,
  WithdrawModal,
} from '@/components/goals/GoalModals'
import { GoalHistory } from '@/components/goals/GoalHistory'
import { GoalProgressChart } from '@/components/goals/GoalProgressChart'
import { GoalsHero } from '@/components/goals/GoalsHero'
import { LoanActionsBar, type LoanMenuItem } from '@/components/loans/LoanActionsBar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'

type Modal = 'none' | 'edit' | 'contribute' | 'withdraw' | 'delete' | 'complete'

function Panel({
  title,
  aside,
  children,
}: {
  title: string
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section aria-label={title} className={cn(PANEL, 'pb-3')}>
      <div className="flex items-baseline justify-between gap-3 pb-1 pt-3.5">
        <h2 className="min-w-0 truncate text-[15px] font-semibold text-[var(--fl-text-1)]">
          {title}
        </h2>
        {aside && <span className="shrink-0 text-xs text-[var(--fl-text-2)]">{aside}</span>}
      </div>
      {children}
    </section>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-t border-[var(--fl-divider)] py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-[var(--fl-text-2)]">{label}</span>
      <span className="min-w-0 text-right font-medium tabular-nums text-[var(--fl-text-1)]">
        {children}
      </span>
    </div>
  )
}

export default function GoalDetailPage() {
  const isReadOnly = useIsReadOnly()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const { formatWhole } = useFormatCurrency()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [modal, setModal] = useState<Modal>('none')
  const close = () => setModal('none')
  const today = new Date()

  const { data: goalsData, isLoading } = useQuery({
    queryKey: ['goals'],
    queryFn: () => listGoals(),
  })
  const goal = id
    ? [
        ...(goalsData?.active ?? []),
        ...(goalsData?.paused ?? []),
        ...(goalsData?.completed ?? []),
      ].find((g) => g.id === id)
    : undefined

  const { data: contributions } = useQuery({
    queryKey: ['goal-contributions', id],
    queryFn: () => listContributions(id!),
    enabled: !!id && !!goal,
  })
  const { data: history } = useQuery({
    queryKey: ['goal-history', id],
    queryFn: () => getGoalHistory(id!),
    enabled: !!id && !!goal,
    // An API without the endpoint yet just means no chart.
    retry: false,
  })
  const { data: earmarks } = useQuery({
    queryKey: ['earmarks', goal?.linkedAccountId],
    queryFn: () => getEarmarks(goal!.linkedAccountId),
    enabled: !!goal && goal.trackingMode === 'EARMARK',
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['goals'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['goal-history', id] })
    queryClient.invalidateQueries({ queryKey: ['earmarks'] })
  }
  const pauseMutation = useMutation({
    mutationFn: () => pauseGoal(id!),
    onSuccess: refresh,
    onError: () => toast.error('Failed to pause goal. Please try again.'),
  })
  const resumeMutation = useMutation({
    mutationFn: () => resumeGoal(id!),
    onSuccess: refresh,
    onError: () => toast.error('Failed to resume goal. Please try again.'),
  })
  const reactivateMutation = useMutation({
    mutationFn: () => reactivateGoal(id!),
    onSuccess: refresh,
    onError: () => toast.error('Failed to reactivate goal. Please try again.'),
  })
  const deleteContributionMutation = useMutation({
    mutationFn: (contributionId: string) => deleteContribution(id!, contributionId),
    onSuccess: () => {
      refresh()
      queryClient.invalidateQueries({ queryKey: ['goal-contributions', id] })
    },
    onError: () => toast.error('Failed to delete contribution. Please try again.'),
  })

  if (isLoading) {
    return <div className="p-6 text-sm text-[var(--fl-text-2)]">Loading…</div>
  }

  if (!goal) {
    return (
      <div className="p-6">
        <p className="text-sm text-[var(--fl-error)]">Goal not found.</p>
        <Button
          variant="link"
          onClick={() => navigate(ROUTES.APP.GOALS)}
          className="mt-2 h-auto p-0 text-[var(--fl-income)]"
        >
          Back to Goals
        </Button>
      </div>
    )
  }

  const isActive = goal.status === 'ACTIVE'
  const isDone = goal.status === 'COMPLETED'
  const remaining = Math.max(fromPaise(toPaise(goal.targetAmount) - toPaise(goal.currentAmount)), 0)
  const badge = isActive ? paceBadge(goal.paceStatus) : null
  const days = goal.targetDate ? daysUntil(goal.targetDate, today) : null

  const note = [
    `${Math.round(Number(goal.progressPercent))}%`,
    remaining > 0 ? `${formatWhole(remaining)} to go` : 'target reached',
    goal.targetDate && days != null && !isDone
      ? days >= 0
        ? `${days} ${days === 1 ? 'day' : 'days'} left (${formatDate(goal.targetDate)})`
        : `${-days} days past ${formatDate(goal.targetDate)}`
      : null,
    goal.status === 'PAUSED' ? 'paused' : isDone ? 'done' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const heroItems = isActive
    ? [
        ...(goal.neededPerMonth != null
          ? [
              {
                label: wide ? 'Needed each month' : 'Needed',
                value: formatWhole(goal.neededPerMonth),
              },
            ]
          : []),
        ...(goal.monthlyPace != null || (badge && goal.paceStatus !== 'NO_DATE')
          ? [
              {
                label: 'Your pace (3 mo)',
                value: goal.monthlyPace != null ? formatWhole(goal.monthlyPace) : '—',
                tone: 'income' as const,
                sub: badge ? (
                  <Badge tone={badge.tone} className="ml-1 align-middle">
                    {badge.label}
                  </Badge>
                ) : undefined,
              },
            ]
          : []),
      ]
    : []

  const menu: LoanMenuItem[] = isReadOnly
    ? []
    : isDone
      ? [
          { label: 'Reactivate', onSelect: () => reactivateMutation.mutate() },
          { label: 'Delete goal', danger: true, onSelect: () => setModal('delete') },
        ]
      : [
          { label: 'Withdraw', onSelect: () => setModal('withdraw') },
          { label: 'Edit', onSelect: () => setModal('edit') },
          isActive
            ? { label: 'Pause', onSelect: () => pauseMutation.mutate() }
            : { label: 'Resume', onSelect: () => resumeMutation.mutate() },
          { label: 'Mark done', onSelect: () => setModal('complete') },
          { label: 'Delete goal', danger: true, onSelect: () => setModal('delete') },
        ]
  const transactionsHref = transactionsLink(
    goal.trackingMode === 'EARMARK'
      ? { ...ALL_TIME, goalId: goal.id }
      : { ...ALL_TIME, financialAccountId: goal.linkedAccountId },
    { back: ROUTES.APP.GOAL_DETAIL(goal.id) }
  )
  const otherGoals = earmarks
    ? fromPaise(
        toPaise(earmarks.earmarked) - toPaise(goal.status === 'ACTIVE' ? goal.currentAmount : 0)
      )
    : 0

  return (
    <div className="flex flex-col gap-3 p-4 pb-24 md:gap-5 md:p-10 lg:pb-10">
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.APP.GOALS)}
          className="h-auto gap-1.5 px-0 py-0 text-sm text-[var(--fl-text-2)] hover:bg-transparent hover:text-[var(--fl-heading)]"
        >
          <ArrowLeft size={14} /> Goals
        </Button>
        <LoanActionsBar
          primary={
            !isReadOnly && !isDone
              ? { label: 'Add money', onClick: () => setModal('contribute') }
              : undefined
          }
          items={menu}
        />
      </div>

      <GoalsHero
        ariaLabel={`${goal.name} progress`}
        eyebrow={
          <span className="flex items-center gap-1.5">
            <CategoryIcon icon={goal.icon || DEFAULT_GOAL_ICON} size={13} />
            {goal.name} · {trackedIn(goal)}
          </span>
        }
        label={wide ? 'Saved' : goal.name}
        amount={goal.currentAmount}
        note={`of ${formatWhole(goal.targetAmount)} · ${note}`}
        percent={Number(goal.progressPercent)}
        timeGone={isActive ? timeGoneFraction(goal.createdAt, goal.targetDate, today) : null}
        items={heroItems}
      />

      {goal.earmarkShortfall > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error-dark)]">
          <span>
            {goal.linkedAccountName} is {formatWhole(goal.earmarkShortfall)} short of what’s set
            aside for its goals.
          </span>
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => setModal('withdraw')}
              className="shrink-0 font-semibold underline"
            >
              Withdraw
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-3 md:gap-[18px] lg:grid-cols-[1.55fr_1fr]">
        <div className="flex flex-col gap-3 md:gap-[18px]">
          {history && history.length > 1 && (
            <Panel title="Progress" aside={`Since ${formatMonth(history[0].month)}`}>
              <GoalProgressChart goal={goal} points={history} />
            </Panel>
          )}
          <Panel
            title="History"
            aside={
              <Link
                to={transactionsHref}
                className="font-semibold text-[var(--fl-primary)] hover:underline"
              >
                All transactions →
              </Link>
            }
          >
            {!contributions || contributions.length === 0 ? (
              <EmptyState
                compact
                icon={PiggyBank}
                title="No contributions yet"
                description="Every amount you put toward this goal will show up here as a running history."
              />
            ) : (
              <GoalHistory
                contributions={contributions}
                onDelete={
                  !isReadOnly && !isDone
                    ? (cid) => deleteContributionMutation.mutate(cid)
                    : undefined
                }
                deleting={deleteContributionMutation.isPending}
              />
            )}
          </Panel>
        </div>

        <div className="flex flex-col gap-3 md:gap-[18px]">
          {goal.trackingMode === 'EARMARK' && earmarks ? (
            <Panel title={goal.linkedAccountName} aside={formatWhole(earmarks.totalBalance)}>
              <div
                aria-hidden="true"
                className="mt-1 flex h-2 overflow-hidden rounded-full bg-[var(--fl-surface-subtle)]"
              >
                <div
                  className={
                    earmarks.available < 0 ? 'bg-[var(--fl-error)]' : 'bg-[var(--fl-hero-bar)]'
                  }
                  style={{
                    width: `${earmarks.totalBalance > 0 ? Math.min(earmarks.earmarked / earmarks.totalBalance, 1) * 100 : 100}%`,
                  }}
                />
              </div>
              <div className="mt-2">
                <Fact label="Set aside for this goal">
                  {formatWhole(isActive ? goal.currentAmount : 0)}
                </Fact>
                <Fact label="Other goals">{formatWhole(otherGoals)}</Fact>
                <Fact label={earmarks.available < 0 ? 'Short' : 'Free to spend'}>
                  <span
                    className={
                      earmarks.available < 0 ? 'text-[var(--fl-error)]' : 'text-[var(--fl-income)]'
                    }
                  >
                    {formatWhole(Math.abs(earmarks.available))}
                  </span>
                </Fact>
              </div>
            </Panel>
          ) : goal.trackingMode === 'DEDICATED' ? (
            <Panel title={goal.linkedAccountName} aside={formatWhole(goal.currentAmount)}>
              <p className="text-[13px] text-[var(--fl-text-2)]">
                The whole balance of this account counts toward the goal.
              </p>
            </Panel>
          ) : null}

          <Panel
            title="Details"
            aside={
              !isReadOnly && !isDone ? (
                <button
                  type="button"
                  onClick={() => setModal('edit')}
                  className="font-semibold text-[var(--fl-primary)] hover:underline"
                >
                  Edit
                </button>
              ) : undefined
            }
          >
            <Fact label="How it’s tracked">
              {goal.trackingMode === 'EARMARK' ? 'Set aside in an account' : 'Own account'}
            </Fact>
            <Fact label="For">{goal.attributedMemberName ?? 'Whole household'}</Fact>
            <Fact label="Started">{formatDate(goal.createdAt)}</Fact>
            {goal.targetDate && <Fact label="Target date">{formatDate(goal.targetDate)}</Fact>}
            {goal.notes && (
              <p className="border-t border-[var(--fl-divider)] pt-2.5 text-[13px] text-[var(--fl-text-2)]">
                {goal.notes}
              </p>
            )}
          </Panel>
        </div>
      </div>

      {modal === 'edit' && <GoalFormSheet goal={goal} onClose={close} />}
      {modal === 'contribute' && <ContributeModal goal={goal} onClose={close} />}
      {modal === 'withdraw' && <WithdrawModal goal={goal} onClose={close} />}
      {modal === 'complete' && <CompleteGoalConfirm goal={goal} onClose={close} />}
      {modal === 'delete' && (
        <DeleteGoalConfirm
          goal={goal}
          onClose={close}
          onDeleted={() => navigate(ROUTES.APP.GOALS)}
        />
      )}
    </div>
  )
}
